"use client";
import { appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { onDarkBase } from "@biom3/design-tokens";
import { Box, ButtCon, ShimmerBox } from "@biom3/react";
import { useIsLessThanMediumScreen } from "@ui-kit/hooks/screenSizeHooks";
import Script from "next/script";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface DevRevContextType {
  openWidget: () => void;
}

const DevRevContext = createContext<DevRevContextType | null>(null);

export const useDevRev = () => {
  const context = useContext(DevRevContext);
  if (!context) {
    throw new Error("useDevRev must be used within DevRevProvider");
  }
  return context;
};

const supportAuthUrl = `${appConfig.IMMUTABLE_BASE_URI}/v1/support/devrev/token`;

interface DevRevInitConfig {
  app_id: string;
  theme: "dark" | "light";
  enable_default_launcher: boolean;
  custom_launcher_selector: string;
  widget_alignment: "left" | "right";
  spacing: { bottom: string; side: string };
  session_token?: string;
}

export function DevRevProvider({ children }: { children: ReactNode }) {
  const appId = process.env.NEXT_PUBLIC_DEVREV_APP_ID;
  const { isLoggedIn, passportClient } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const isLessThanMediumScreen = useIsLessThanMediumScreen();

  const eventHandlerRef = useRef<
    ((payload: { type: string; data?: unknown }) => void) | null
  >(null);
  const tokenPromiseRef = useRef<Promise<string | null> | null>(null);
  const [showDevRevBox, setShowDevRevBox] = useState(false);
  const [devRevInitError, setDevRevInitError] = useState(false);

  const handleCloseDevRevBox = useCallback(() => {
    setShowDevRevBox(false);
    setDevRevInitError(false);
    tokenPromiseRef.current = null;
    // Optionally, clean up SDK state if needed
  }, []);

  /**
   * Get a valid DevRev token. This function is memoized to prevent multiple
   * concurrent requests.
   */
  const getDevRevToken = useCallback(async (): Promise<string | null> => {
    try {
      const accessToken = await passportClient.getAccessToken();
      if (!accessToken) {
        throw new Error("Could not get passport access token");
      }
      const response = await fetch(supportAuthUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch DevRev token: ${response.statusText}`);
      }
      const data = await response.json();
      return data.access_token;
    } catch (error: unknown) {
      console.error("Error getting DevRev token:", error);
      tokenPromiseRef.current = null;
      return null;
    }
  }, [passportClient]);

  // Pre-fetch DevRev token when user logs in
  useEffect(() => {
    if (isLoggedIn && passportClient && appId) {
      if (!tokenPromiseRef.current) {
        tokenPromiseRef.current = getDevRevToken();
      }
    }
  }, [isLoggedIn, passportClient, getDevRevToken, appId]);

  /**
   * Handle DevRev events
   */
  const handleDevRevEvent = useCallback(
    (payload: { type: string; data?: unknown }) => {
      try {
        switch (payload.type) {
          case "ON_PLUG_WIDGET_READY":
            setShowDevRevBox(false);
            setDevRevInitError(false);
            if (eventHandlerRef.current && window.plugSDK.offEvent) {
              window.plugSDK.offEvent(eventHandlerRef.current);
              eventHandlerRef.current = null;
            }
            window.plugSDK.toggleWidget(true);
            break;
          default:
            break;
        }
      } catch (error: unknown) {
        console.error("DevRev event handler error:", error);
      }
    },
    [],
  );

  /**
   * Initialize DevRev SDK with token
   */
  const initializeDevRevSDK = useCallback(
    async (token: string) => {
      if (isLessThanMediumScreen) {
        return;
      }
      try {
        if (eventHandlerRef.current && window.plugSDK.offEvent) {
          window.plugSDK.offEvent(eventHandlerRef.current);
          eventHandlerRef.current = null;
        }

        const initConfig: DevRevInitConfig = {
          app_id: appId as string,
          theme: "dark",
          enable_default_launcher: false,
          custom_launcher_selector: "#devrev-widget-container",
          widget_alignment: "right",
          spacing: { bottom: "40px", side: "40px" },
          session_token: token,
        };

        window.plugSDK.init(initConfig);
        window.plugSDK.isInitialized = true;

        eventHandlerRef.current = handleDevRevEvent;
        window.plugSDK.onEvent(eventHandlerRef.current);
        setDevRevInitError(false);
      } catch (error: unknown) {
        setDevRevInitError(true);
        tokenPromiseRef.current = null;
        console.error("Failed to initialize DevRev SDK:", error);
        throw new Error(
          `SDK initialization failed: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        );
      }
    },
    [appId, handleDevRevEvent, isLessThanMediumScreen],
  );

  /**
   * Contains the core logic for initializing and opening the widget
   * once the user is authenticated.
   */
  const openWidgetAfterAuth = useCallback(async () => {
    setShowDevRevBox(true);
    try {
      if (window.plugSDK?.isWidgetOpen) {
        setShowDevRevBox(false);
        return;
      }

      if (window.plugSDK?.isInitialized) {
        setShowDevRevBox(false);
        window.plugSDK.toggleWidget(true);
        return;
      }

      if (!tokenPromiseRef.current) {
        tokenPromiseRef.current = getDevRevToken();
      }
      const token = await tokenPromiseRef.current;

      if (token) {
        await initializeDevRevSDK(token);
      } else {
        setShowDevRevBox(false);
        console.error("Failed to get DevRev token on-demand.");
      }
    } catch (error: unknown) {
      setShowDevRevBox(false);
      console.error("Failed to open widget:", error);
    }
  }, [getDevRevToken, initializeDevRevSDK]);

  /**
   * Handle Help button click
   */
  const openWidget = useCallback(async () => {
    if (isLessThanMediumScreen) {
      return;
    }
    if (!isLoggedIn) {
      openLoginModal(openWidgetAfterAuth);
      return;
    }
    await openWidgetAfterAuth();
  }, [isLoggedIn, openLoginModal, openWidgetAfterAuth, isLessThanMediumScreen]);

  const contextValue = useMemo(() => ({ openWidget }), [openWidget]);

  return (
    <DevRevContext.Provider value={contextValue}>
      {isLoggedIn && !isLessThanMediumScreen && (
        <Script
          src="https://plug-platform.devrev.ai/static/plug.js"
          strategy="lazyOnload"
          onError={(error) => console.error("DevRev: Script error", error)}
        />
      )}

      {children}
      <div id="devrev-widget-container" />

      {showDevRevBox && (
        <Box
          sx={{
            position: "fixed",
            bottom: onDarkBase.spacing.x10, // 40px
            right: onDarkBase.spacing.x10, // 40px
            width: "448px",
            height: "720px",
            backgroundColor: onDarkBase.color.brand[2], // #131313
            padding: onDarkBase.spacing.x4, // 16px
            borderRadius: onDarkBase.borderRadius.x8, // 8px
            border: `1px solid ${onDarkBase.color.neutral[800]}`,
            boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
          }}
        >
          <Box sx={{ position: "relative" }}>
            <ShimmerBox
              sx={{
                width: "calc(100% - 64px)",
                height: "48px",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            />
            {devRevInitError ? (
              <ButtCon
                icon="Close"
                onClick={handleCloseDevRevBox}
                sx={{
                  position: "absolute",
                  top: "0",
                  right: "0",
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                variant="tertiary"
                size="medium"
                aria-label="Close DevRev Box"
              />
            ) : (
              <ShimmerBox
                sx={{
                  position: "absolute",
                  top: "0",
                  right: "0",
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                }}
              />
            )}
          </Box>
          <ShimmerBox
            sx={{
              width: "100%",
              height: "497px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          />
          <ShimmerBox
            sx={{
              width: "100%",
              height: "109px",
              borderRadius: "8px",
            }}
          />
        </Box>
      )}
    </DevRevContext.Provider>
  );
}
